import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import {
  LogOut, Download, Search, Filter, RefreshCw, Mail, Phone, MapPin,
  Calendar, CheckCircle, Clock, XCircle, Eye, Leaf
} from 'lucide-react';
import { toast } from 'sonner';
import AdminRealisations from './admin/AdminRealisations';
import AdminSettings from './admin/AdminSettings';

const AdminDashboard = ({ user, onLogout }) => {
  const [quotes, setQuotes] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedQuote, setSelectedQuote] = useState(null);
  const navigate = useNavigate();

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Fetch dashboard stats
  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/stats`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  // Fetch quotes
  const fetchQuotes = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') {
        params.append('status_filter', statusFilter);
      }
      if (sourceFilter !== 'all') {
        params.append('source_filter', sourceFilter);
      }
      if (searchQuery) {
        params.append('search', searchQuery);
      }
      params.append('limit', '100');

      const response = await fetch(`${API_URL}/api/admin/quotes?${params}`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setQuotes(data.quotes || []);
      } else if (response.status === 401) {
        toast.error('Session expirée. Veuillez vous reconnecter.');
        onLogout();
        navigate('/admin/login');
      }
    } catch (error) {
      console.error('Error fetching quotes:', error);
      toast.error('Erreur lors du chargement des demandes');
    } finally {
      setLoading(false);
    }
  };

  // Update quote status
  const updateQuoteStatus = async (quoteId, newStatus) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/quotes/${quoteId}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ status: newStatus })
      });

      if (response.ok) {
        toast.success('Statut mis à jour avec succès');
        fetchQuotes();
        fetchStats();
      } else {
        toast.error('Erreur lors de la mise à jour du statut');
      }
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Erreur lors de la mise à jour du statut');
    }
  };

  // Export to CSV
  const exportToCSV = async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') {
        params.append('status_filter', statusFilter);
      }
      if (sourceFilter !== 'all') {
        params.append('source_filter', sourceFilter);
      }

      const response = await fetch(`${API_URL}/api/admin/quotes/export?${params}`, {
        credentials: 'include'
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `vireo_quotes_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success('Export réussi !');
      } else {
        toast.error('Erreur lors de l\'export');
      }
    } catch (error) {
      console.error('Error exporting:', error);
      toast.error('Erreur lors de l\'export');
    }
  };

  // Download an uploaded document (admin only)
  const downloadFile = async (fileId, filename) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/files/${fileId}/download`, {
        credentials: 'include'
      });
      if (!response.ok) {
        toast.error('Téléchargement impossible');
        return;
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || 'document';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Erreur de téléchargement');
    }
  };

  // Logout
  const handleLogout = async () => {
    try {
      await fetch(`${API_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
      toast.success('Déconnexion réussie');
      onLogout(); // Clear user state first
      window.location.href = '/admin/login'; // Force full page reload to login
    } catch (error) {
      console.error('Logout error:', error);
      onLogout();
      window.location.href = '/admin/login';
    }
  };

  useEffect(() => {
    fetchQuotes();
    fetchStats();
  }, [statusFilter, sourceFilter, searchQuery]);

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { label: 'En attente', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
      contacted: { label: 'Contacté', color: 'bg-blue-100 text-blue-800', icon: Phone },
      completed: { label: 'Terminé', color: 'bg-green-100 text-green-800', icon: CheckCircle },
      rejected: { label: 'Rejeté', color: 'bg-red-100 text-red-800', icon: XCircle }
    };

    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;

    return (
      <Badge className={`${config.color} flex items-center gap-1`}>
        <Icon className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Leaf className="h-8 w-8 text-emerald-600" />
              <div>
                <h1 className="text-2xl font-bold text-slate-900">VIREO Admin</h1>
                <p className="text-sm text-slate-600">Dashboard de gestion</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="text-sm font-medium text-slate-900">{user?.name || user?.email}</p>
                <p className="text-xs text-slate-600">{user?.role}</p>
              </div>
              <Button
                onClick={handleLogout}
                variant="outline"
                className="flex items-center gap-2"
              >
                <LogOut className="h-4 w-4" />
                Déconnexion
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8">
        <Tabs defaultValue="demandes" className="w-full">
          <TabsList className="mb-6">
            <TabsTrigger value="demandes" data-testid="tab-demandes">Demandes</TabsTrigger>
            <TabsTrigger value="realisations" data-testid="tab-realisations">Réalisations</TabsTrigger>
            <TabsTrigger value="parametres" data-testid="tab-parametres">Paramètres</TabsTrigger>
          </TabsList>

          <TabsContent value="demandes">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-slate-600">Total Demandes</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-slate-900">{stats.total}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-slate-600">En attente</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-yellow-600">{stats.pending}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-slate-600">Contactés</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-blue-600">{stats.contacted}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-slate-600">Terminés</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-green-600">{stats.completed}</div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Suivi des devis & conversion */}
        {stats && (
          <Card className="mb-8" data-testid="conversion-tracker">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-semibold text-slate-900">Suivi des devis</CardTitle>
              <p className="text-sm text-slate-500">Pilotez votre acquisition : des demandes reçues à la conversion.</p>
            </CardHeader>
            <CardContent>
              {/* Entonnoir */}
              <div className="flex flex-col md:flex-row items-stretch gap-3 mb-6">
                <div className="flex-1 rounded-lg bg-slate-50 border border-slate-200 p-4 text-center" data-testid="funnel-received">
                  <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">Reçues</div>
                  <div className="text-3xl font-bold text-slate-900">{stats.total}</div>
                </div>
                <div className="hidden md:flex items-center text-slate-300 text-2xl">→</div>
                <div className="flex-1 rounded-lg bg-amber-50 border border-amber-200 p-4 text-center" data-testid="funnel-followed-up">
                  <div className="text-xs uppercase tracking-wide text-amber-700 mb-1">Relancées</div>
                  <div className="text-3xl font-bold text-amber-600">{stats.followed_up ?? 0}</div>
                </div>
                <div className="hidden md:flex items-center text-slate-300 text-2xl">→</div>
                <div className="flex-1 rounded-lg bg-emerald-50 border border-emerald-200 p-4 text-center" data-testid="funnel-converted">
                  <div className="text-xs uppercase tracking-wide text-emerald-700 mb-1">Converties</div>
                  <div className="text-3xl font-bold text-emerald-600">{stats.completed}</div>
                </div>
              </div>
              {/* Taux de conversion + rejetées */}
              <div className="grid grid-cols-2 gap-4">
                <div className="rounded-lg border border-slate-200 p-4" data-testid="conversion-rate">
                  <div className="text-sm text-slate-500 mb-1">Taux de conversion</div>
                  <div className="flex items-end gap-2">
                    <span className="text-3xl font-bold text-emerald-600">{stats.conversion_rate ?? 0}%</span>
                    <span className="text-xs text-slate-400 mb-1">des demandes reçues</span>
                  </div>
                  <div className="mt-2 h-2 w-full rounded-full bg-slate-100 overflow-hidden">
                    <div className="h-full bg-emerald-500 rounded-full transition-all" style={{ width: `${Math.min(stats.conversion_rate ?? 0, 100)}%` }} />
                  </div>
                </div>
                <div className="rounded-lg border border-slate-200 p-4" data-testid="rejected-count">
                  <div className="text-sm text-slate-500 mb-1">Non retenues</div>
                  <div className="text-3xl font-bold text-slate-400">{stats.rejected ?? 0}</div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Filters and Actions */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="flex flex-col md:flex-row gap-4 items-end">
              <div className="flex-1">
                <label className="text-sm font-medium text-slate-700 mb-2 block">Rechercher</label>
                <div className="relative">
                  <Search className="absolute left-3 top-3 h-5 w-5 text-slate-400" />
                  <Input
                    placeholder="Nom, email ou ville..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
              <div className="w-full md:w-48">
                <label className="text-sm font-medium text-slate-700 mb-2 block">Statut</label>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les statuts</SelectItem>
                    <SelectItem value="pending">En attente</SelectItem>
                    <SelectItem value="contacted">Contacté</SelectItem>
                    <SelectItem value="completed">Terminé</SelectItem>
                    <SelectItem value="rejected">Rejeté</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="w-full md:w-48">
                <label className="text-sm font-medium text-slate-700 mb-2 block">Provenance</label>
                <Select value={sourceFilter} onValueChange={setSourceFilter}>
                  <SelectTrigger data-testid="source-filter-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Toutes provenances</SelectItem>
                    <SelectItem value="contact">Site web</SelectItem>
                    <SelectItem value="partenaires">Pro / Partenaire</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button
                onClick={() => { fetchQuotes(); fetchStats(); }}
                variant="outline"
                className="flex items-center gap-2"
              >
                <RefreshCw className="h-4 w-4" />
                Actualiser
              </Button>
              <Button
                onClick={exportToCSV}
                className="bg-emerald-600 hover:bg-emerald-700 flex items-center gap-2"
              >
                <Download className="h-4 w-4" />
                Exporter CSV
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Quotes Table */}
        <Card>
          <CardHeader>
            <CardTitle>Demandes de Devis ({quotes.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-12">
                <RefreshCw className="h-8 w-8 animate-spin text-emerald-600 mx-auto mb-4" />
                <p className="text-slate-600">Chargement...</p>
              </div>
            ) : quotes.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-slate-600">Aucune demande trouvée</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Date</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Client</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Contact</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Ville</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Type de Projet</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Budget</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Provenance</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Statut</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {quotes.map((quote) => (
                      <tr key={quote.id} className="hover:bg-slate-50 transition-colors" data-testid={`quote-row-${quote.id}`}>
                        <td className="px-4 py-3 text-sm text-slate-600">
                          <div className="flex items-center gap-2">
                            <Calendar className="h-4 w-4 text-slate-400" />
                            {formatDate(quote.created_at)}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="font-medium text-slate-900">{quote.name}</div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-sm space-y-1">
                            <div className="flex items-center gap-2 text-slate-600">
                              <Mail className="h-3 w-3" />
                              <a href={`mailto:${quote.email}`} className="hover:text-emerald-600">
                                {quote.email}
                              </a>
                            </div>
                            <div className="flex items-center gap-2 text-slate-600">
                              <Phone className="h-3 w-3" />
                              <a href={`tel:${quote.phone}`} className="hover:text-emerald-600">
                                {quote.phone}
                              </a>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2 text-sm text-slate-600">
                            <MapPin className="h-4 w-4 text-slate-400" />
                            {quote.city}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-700">
                          {quote.project_type}
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-700" data-testid={`quote-budget-${quote.id}`}>
                          {quote.budget || '—'}
                        </td>
                        <td className="px-4 py-3" data-testid={`quote-source-${quote.id}`}>
                          {quote.source === 'partenaires' ? (
                            <Badge className="bg-cyan-100 text-cyan-800 hover:bg-cyan-100">Pro / Partenaire</Badge>
                          ) : (
                            <Badge className="bg-slate-100 text-slate-700 hover:bg-slate-100">Site web</Badge>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <Select
                            value={quote.status}
                            onValueChange={(value) => updateQuoteStatus(quote.id, value)}
                          >
                            <SelectTrigger className="w-32">
                              {getStatusBadge(quote.status)}
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="pending">En attente</SelectItem>
                              <SelectItem value="contacted">Contacté</SelectItem>
                              <SelectItem value="completed">Terminé</SelectItem>
                              <SelectItem value="rejected">Rejeté</SelectItem>
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="px-4 py-3">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setSelectedQuote(quote)}
                            data-testid={`quote-details-${quote.id}`}
                            className="flex items-center gap-2"
                          >
                            <Eye className="h-4 w-4" />
                            Détails
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
          </TabsContent>

          <TabsContent value="realisations">
            <AdminRealisations />
          </TabsContent>

          <TabsContent value="parametres">
            <AdminSettings />
          </TabsContent>
        </Tabs>
      </div>

      {/* Quote Details Modal */}
      {selectedQuote && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" onClick={() => setSelectedQuote(null)}>
          <Card className="max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Détails de la Demande</CardTitle>
                <Button variant="ghost" size="sm" onClick={() => setSelectedQuote(null)}>
                  ✕
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm font-semibold text-slate-700">Client</label>
                <p className="text-lg text-slate-900">{selectedQuote.name}</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-semibold text-slate-700">Email</label>
                  <p className="text-slate-900">{selectedQuote.email}</p>
                </div>
                <div>
                  <label className="text-sm font-semibold text-slate-700">Téléphone</label>
                  <p className="text-slate-900">{selectedQuote.phone}</p>
                </div>
              </div>
              <div>
                <label className="text-sm font-semibold text-slate-700">Ville</label>
                <p className="text-slate-900">{selectedQuote.city}</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-semibold text-slate-700">Type de demande</label>
                  <p className="text-slate-900">{selectedQuote.client_type || '—'}</p>
                </div>
                <div>
                  <label className="text-sm font-semibold text-slate-700">Source</label>
                  <p className="text-slate-900">{selectedQuote.source === 'partenaires' ? 'Pro / Partenaire' : 'Site web'}</p>
                </div>
                <div>
                  <label className="text-sm font-semibold text-slate-700">Type de travaux</label>
                  <p className="text-slate-900">{selectedQuote.project_type}</p>
                </div>
                <div>
                  <label className="text-sm font-semibold text-slate-700">Budget estimé</label>
                  <p className="text-slate-900">{selectedQuote.budget || '—'}</p>
                </div>
                {selectedQuote.company && (
                  <div>
                    <label className="text-sm font-semibold text-slate-700">Société</label>
                    <p className="text-slate-900">{selectedQuote.company}</p>
                  </div>
                )}
                {selectedQuote.role && (
                  <div>
                    <label className="text-sm font-semibold text-slate-700">Profession</label>
                    <p className="text-slate-900">{selectedQuote.role}</p>
                  </div>
                )}
              </div>
              <div>
                <label className="text-sm font-semibold text-slate-700">Message</label>
                <p className="text-slate-700 whitespace-pre-wrap bg-slate-50 p-4 rounded-lg">
                  {selectedQuote.message}
                </p>
              </div>
              {selectedQuote.files && selectedQuote.files.length > 0 && (
                <div>
                  <label className="text-sm font-semibold text-slate-700">
                    Pièces jointes ({selectedQuote.files.length})
                  </label>
                  <ul className="mt-2 space-y-2">
                    {selectedQuote.files.map((f) => (
                      <li key={f.id} className="flex items-center justify-between gap-3 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
                        <span className="text-sm text-slate-700 truncate">{f.original_filename}</span>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => downloadFile(f.id, f.original_filename)}
                          data-testid={`download-file-${f.id}`}
                          className="flex items-center gap-2 flex-shrink-0"
                        >
                          <Download className="h-4 w-4" />
                          Télécharger
                        </Button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-semibold text-slate-700">Date de demande</label>
                  <p className="text-slate-900">{formatDate(selectedQuote.created_at)}</p>
                </div>
                <div>
                  <label className="text-sm font-semibold text-slate-700">Statut</label>
                  <div className="mt-1">
                    {getStatusBadge(selectedQuote.status)}
                  </div>
                </div>
              </div>
              <div className="flex gap-2 pt-4 border-t">
                <Button
                  onClick={() => window.location.href = `mailto:${selectedQuote.email}`}
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                >
                  <Mail className="h-4 w-4 mr-2" />
                  Envoyer un email
                </Button>
                <Button
                  onClick={() => window.location.href = `tel:${selectedQuote.phone}`}
                  variant="outline"
                  className="flex-1"
                >
                  <Phone className="h-4 w-4 mr-2" />
                  Appeler
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
